import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import DashboardLayout from "./DashboardLayout";
import { formatKolkataDateTime } from "../utils/dateTime";
import { API_BASE } from "../utils/apiBase";
import { RUNTIME_PUBLIC_BASE } from "../utils/runtimeBase";
import { exportLoadMoneyCsv, exportLoadMoneyExcel, exportLoadMoneyPdf } from "../utils/loadMoneyExportUtils";

const API = API_BASE;
const uploadsBase = `${RUNTIME_PUBLIC_BASE}/uploads`;

const pageStyle = { display: "grid", gap: "20px" };
const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)",
  borderRadius: "28px",
  padding: "28px",
  color: "#ffffff",
  boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)",
};
const panelStyle = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};
const tableWrap = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thStyle = { textAlign: "left", padding: "14px 12px", color: "#7c2d3a", borderBottom: "1px solid rgba(148, 163, 184, 0.16)", fontSize: "13px", background: "rgba(255,247,237,0.72)" };
const tdStyle = { padding: "14px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#0f172a", verticalAlign: "top" };

const resolveReceiptUrl = (receiptFile) => {
  const raw = String(receiptFile || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
  const clean = raw.replace(/^\/+/, "");
  if (clean.startsWith("uploads/")) return `${RUNTIME_PUBLIC_BASE}/${clean}`;
  if (clean.startsWith("load-money-receipts/")) return `${uploadsBase}/${clean}`;
  return `${uploadsBase}/load-money-receipts/${clean}`;
};

function ViewMoneyRequest() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [perPage, setPerPage] = useState("10");
  const [currentPage, setCurrentPage] = useState(1);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/load-money-requests`, {
        params: { _ts: Date.now() },
      });
      setRows((Array.isArray(res.data) ? res.data : []).filter((row) => row.status === "approved"));
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const { pagedRows, totalRows, totalPages, pageSize, safeCurrentPage, startIndex } = useMemo(() => {
    const total = rows.length;
    const size = perPage === "all" ? Math.max(1, total) : Number(perPage);
    const pages = Math.max(1, Math.ceil(total / size));
    const current = Math.min(currentPage, pages);
    const start = (current - 1) * size;
    const list = perPage === "all" ? rows : rows.slice(start, start + size);
    return {
      pagedRows: list,
      totalRows: total,
      totalPages: pages,
      pageSize: size,
      safeCurrentPage: current,
      startIndex: start,
    };
  }, [rows, perPage, currentPage]);

  return (
    <DashboardLayout>
      <div style={pageStyle}>
        <section style={heroStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ maxWidth: "720px" }}>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>
                Payment Workspace
              </p>
              <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>Approve Request</h1>
            </div>
            <div style={{ width: "92px", height: "92px", borderRadius: "26px", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "38px" }}>
              <i className="bi bi-bank2"></i>
            </div>
          </div>
        </section>

        <section style={panelStyle}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h3 style={{ margin: 0, color: "#0f172a" }}>Approved Requests</h3>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={!rows.length}
                onClick={() => exportLoadMoneyCsv({ rows, fileName: "approved-requests.csv", dateField: "approved_at", dateLabel: "Approved At" })}
              >
                CSV
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-success"
                disabled={!rows.length}
                onClick={() => exportLoadMoneyExcel({ rows, fileName: "approved-requests.xls", title: "Approved Requests", dateField: "approved_at", dateLabel: "Approved At" })}
              >
                Excel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                disabled={!rows.length}
                onClick={() => exportLoadMoneyPdf({ rows, fileName: "approved-requests.pdf", title: "Approved Requests", dateField: "approved_at", dateLabel: "Approved At" })}
              >
                PDF
              </button>
              <span className="small text-muted ms-1">Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "120px" }}
                value={perPage}
                onChange={(event) => {
                  setPerPage(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="all">Show All</option>
              </select>
            </div>
          </div>

          <div style={tableWrap}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Franchise</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Payment</th>
                  <th style={thStyle}>Reference</th>
                  <th style={thStyle}>Receipt</th>
                  <th style={thStyle}>Franchise Note</th>
                  <th style={thStyle}>Admin Note</th>
                  <th style={thStyle}>Approved At</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td style={tdStyle} colSpan={8}>Loading...</td></tr>
                ) : pagedRows.length ? (
                  pagedRows.map((row) => (
                    <tr key={row.id}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700 }}>{row.centre_name || row.login_id || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: "12px", marginTop: "4px" }}>{row.centre_code || row.contact || "-"}</div>
                      </td>
                      <td style={tdStyle}>Rs {Number(row.amount || 0).toFixed(2)}</td>
                      <td style={{ ...tdStyle, textTransform: "capitalize" }}>{String(row.payment_mode || "-").replaceAll("_", " ")}</td>
                      <td style={tdStyle}>{row.reference_no || "-"}</td>
                      <td style={tdStyle}>
                        {row.receipt_file ? (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setReceiptPreview({ url: resolveReceiptUrl(row.receipt_file) })}>
                            <i className="bi bi-eye-fill"></i>
                          </button>
                        ) : "-"}
                      </td>
                      <td style={tdStyle}>{row.note || "-"}</td>
                      <td style={tdStyle}>{row.admin_note || "-"}</td>
                      <td style={tdStyle}>{formatKolkataDateTime(row.approved_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td style={tdStyle} colSpan={8}>No approved money requests found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {perPage !== "all" && totalPages > 1 ? (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="small text-muted">
                Showing {startIndex + 1} - {Math.min(startIndex + pageSize, totalRows)} of {totalRows}
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={safeCurrentPage <= 1}
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                >
                  Prev
                </button>
                <span className="small text-muted">Page {safeCurrentPage} / {totalPages}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </section>

        {receiptPreview
          ? createPortal(
              <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 99999 }} onClick={() => setReceiptPreview(null)}>
                <div style={{ background: "#fff", borderRadius: "22px", width: "min(640px, 100%)", maxHeight: "82vh", overflow: "auto", padding: "16px", boxShadow: "0 28px 80px rgba(15, 23, 42, 0.25)" }} onClick={(event) => event.stopPropagation()}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                    <h3 style={{ margin: 0, color: "#0f172a" }}>Receipt Preview</h3>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setReceiptPreview(null)}>Close</button>
                  </div>
                  {receiptPreview.url.toLowerCase().endsWith(".pdf") ? (
                    <iframe title="Receipt Preview" src={receiptPreview.url} style={{ width: "100%", height: "62vh", border: "none", borderRadius: "14px" }} />
                  ) : (
                    <img src={receiptPreview.url} alt="Receipt Preview" style={{ width: "100%", maxHeight: "62vh", objectFit: "contain", borderRadius: "14px" }} />
                  )}
                </div>
              </div>,
              document.body
            )
          : null}
      </div>
    </DashboardLayout>
  );
}

export default ViewMoneyRequest;



